import React from "react";
import { Box, Typography, CardContent, Divider } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { AnnouncementStatsDto } from "../../../shared/dtos/announcement/AnnouncementStatsDto";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#e91e63",
];

const formatAudience = (audienceStr: string): string => {
  if (!audienceStr) return "";
  return audienceStr
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

interface AnnouncementOverviewWidgetProps {
  data: AnnouncementStatsDto;
}

const AnnouncementOverviewWidget: React.FC<AnnouncementOverviewWidgetProps> = ({
  data,
}) => {
  const meta = getEntityMeta("ANNOUNCEMENT");
  const Icon = meta.icon;

  const chartData =
    data.activeByAudience?.map((item) => ({
      name: formatAudience(item.audience),
      value: item.count,
    })) || [];

  return (
    <StyledCard
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <CardContent
        sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {meta.title}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            my: 1,
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "secondary.main" }}
            >
              {data.totalActive}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Active Now
            </Typography>
          </Box>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "info.main" }}
            >
              {data.scheduledForFuture}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Scheduled
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {chartData.length > 0 ? (
          <Box
            sx={{
              flexGrow: 1,
              width: "100%",
              minHeight: 180,
              position: "relative",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                display: "block",
                textAlign: "center",
                mb: 1,
              }}
            >
              Active by Audience
            </Typography>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((_entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexGrow: 1,
              minHeight: 180,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontStyle: "italic" }}
            >
              No active audience data.
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};
export default AnnouncementOverviewWidget;
